import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  MediaSignal,
  MediaSignalBulletin,
  MediaSignalCluster,
  MediaSignalFreshness,
} from '@tsr/shared-types';
import { findHazardLexiconEntry, HAZARD_LEXICON } from '@tsr/shared-types';
import {
  buildBulletinQuery,
  buildClusterQuery,
  buildSignalQuery,
  computeFeedFreshness,
  corroboratingOfficialSignals,
  DEFAULT_SIGNAL_FILTERS,
  hasSignalMedia,
  isOfficialSignal,
  officialSourceLabel,
  routeSignalsByStrength,
  SINCE_CHOICES,
  signalBodyExcerpt,
  signalDisplayTitle,
  signalSourceLabel,
  signalTime,
  type SignalFeedFilters,
  type SignalFeedFocus,
} from './lib/mediaSignalFeed';
import { logAction, getSessionId } from './lib/operatorActions';
import { usePinnedFeeds } from './pinnedFeeds';
import { useNewsFocus } from './newsFocus';
import { useNowTick } from './useNowTick';
import { AskTheWire } from './AskTheWire';
import * as V from './feedVocabulary';

/**
 * News & Social feed (#65/#183 wave 3b; unified feed #555) — the shared surface
 * behind BOTH operator homes: the COP left-rail "News" tab and the operations-
 * panel "News & Social" tab (behind the protected queues). It reads the 0069
 * media_signals wire through GET /api/media-signals.
 *
 * UNIFIED FEED (#555): the wire is presented as ONE strictly time-sorted stream
 * below a CONDITIONAL pinned zone — replacing the old multi-lane surface (the
 * #296 surge lane + #373 headline lane + #361 credible-media promotion). The
 * read-time strength split (routeSignalsByStrength, #373) is unchanged and still
 * feeds the pinned zone's urgent-official band; only the presentation collapses.
 *
 *   • PINNED ZONE (top, renders ONLY when it has content — PART IX contextual-
 *     when-relevant): the STRONG-OFFICIAL urgent band (#373 — CCCS cyber / GoC
 *     recalls / PHAC flagged authoritative AND about an immediate physical
 *     hazard, read as verified authority + operator review, never an auto-scored
 *     Response Level); the Emerging Situation Brief cards whose surge anomaly is
 *     still LIVE (#295/#561 — active/confirmed; a resolved/dismissed/orphan brief
 *     demotes into the stream below, see #561); and the surge-volume absorbers —
 *     the #349 surge count-cards + the collapsed Raw chatter section — which live
 *     WITH the live briefs rather than in the stream.
 *   • ONE STREAM below: the individual posts — cluster-matched surge posts,
 *     the headline wire (GDELT / RSS), the WEAK official alerts folded in
 *     (keeping their OFFICIAL provenance), and any DEMOTED brief whose surge has
 *     resolved (#561) — MERGED and RE-SORTED strictly by time DESC (posts by
 *     signal time, briefs by generated_at). Each card keeps its own identity (✓ official emerald
 *     badge + issuing host · violet "trending · not confirmed" on social · a
 *     "✓ officials reporting" corroboration chip when a recent official feed
 *     reports the same hazard family · a "📸 media" chip when a photo/video is
 *     attached). Ordering is chronological only — a media post is NEVER promoted.
 *
 * Nothing here auto-urgents or scores a Response Level (design-council PART IX);
 * everything stays ambient + operator-gated:
 *   • provenance-forward cards — source, language, published time, link out —
 *     never a bare unattributed claim;
 *   • PIN (promotion v1) rides the existing kind:'news' pin path into the
 *     panel media region / wall mosaic, audited via 'pin_media_signal';
 *   • DISMISS hides a signal everywhere (server-side dismissals store,
 *     migration 0071 — survives reload, shared across windows), audited via
 *     'dismiss_media_signal'; a transient "Dismissed — Undo" beat (#251)
 *     offers one undo for a few seconds (DELETE the dismissal row, audited
 *     via 'restore_media_signal') — a beat, not a persistent affordance;
 *   • EVENT MODE (raw firehose, OFF by default) drops the narrowing filters;
 *     the wire itself stays lexicon-gated in v1;
 *   • an incident-focus bbox arrives over the news-focus bridge (#183 chip)
 *     and renders as a clearable chip — navigation, never persisted.
 *
 * Feed fetches are `cache: 'no-store'`: the route serves max-age=30 for
 * ambient consumers, but here a dismiss must not resurrect on a quick reload.
 */

const POLL_MS = 60_000;

/** #561 PIN LIFECYCLE — a brief stays in the pinned zone only while its surge
 *  anomaly is LIVE. The two live states from the anomaly status machine
 *  (active|confirmed|dismissed|resolved); once the anomaly resolves (the shipped
 *  35-min chatter TTL) or is dismissed — or was never raised (suppressed → null
 *  FK) — the brief drops into the time-sorted stream at its generated_at. */
const LIVE_ANOMALY_STATUSES = new Set(['active', 'confirmed']);

/** Lifetime of the transient "Dismissed — Undo" beat (#251). PART IX: a beat,
 *  not a persistent affordance — after this it's the include_dismissed
 *  escape hatch or nothing. */
const UNDO_BEAT_MS = 6_000;

type FeedState = 'loading' | 'ready' | 'error';

type Tab = 'briefs' | 'news';

/** Split social-surge posts into the ones inside a returned #349 cluster (they
 *  stream) and the rest (raw chatter). */
function partitionSurge(
  surge: readonly MediaSignal[],
  clusterKeys: ReadonlySet<string>,
): { clusteredSurge: MediaSignal[]; rawChatter: MediaSignal[] } {
  const clusteredSurge: MediaSignal[] = [];
  const rawChatter: MediaSignal[] = [];
  for (const s of surge) {
    if (s.inSurgeCluster && clusterKeys.has(s.surgeClusterKey ?? '')) clusteredSurge.push(s);
    else rawChatter.push(s);
  }
  return { clusteredSurge, rawChatter };
}

function byTimeDesc(a: MediaSignal, b: MediaSignal): number {
  return new Date(signalTime(b)).getTime() - new Date(signalTime(a)).getTime();
}

export function NewsSocialFeed({ surface }: { surface: 'rail' | 'panel' }): JSX.Element {
  const [filters, setFilters] = useState<SignalFeedFilters>(DEFAULT_SIGNAL_FILTERS);
  const [eventMode, setEventMode] = useState(false);
  const [focus, setFocus] = useState<SignalFeedFocus | null>(null);
  const [signals, setSignals] = useState<MediaSignal[]>([]);
  // The DISPLAYED snapshot the streams render — frozen between reveals so a poll
  // never AUTO-INSERTS a card into a stream the operator is reading (#563). The
  // band / live briefs / count-cards read `signals` (latest) + update in place.
  const [displayed, setDisplayed] = useState<MediaSignal[]>([]);
  const [bulletins, setBulletins] = useState<MediaSignalBulletin[]>([]);
  const [clusters, setClusters] = useState<MediaSignalCluster[]>([]);
  const [state, setState] = useState<FeedState>('loading');
  // Last-good SERVER freshness envelope + when we received it (for the skew-free,
  // ticking staleness computation — an API kill keeps the last-good stamp).
  const [freshness, setFreshness] = useState<{ data: MediaSignalFreshness | null; receivedAt: number }>(
    () => ({ data: null, receivedAt: Date.now() }),
  );
  const [activeTab, setActiveTab] = useState<Tab>('news');
  const [briefsBlink, setBriefsBlink] = useState(false);
  const [highlightIds, setHighlightIds] = useState<ReadonlySet<string>>(() => new Set());
  const { toggle: togglePin, isPinned } = usePinnedFeeds();
  const nowMs = useNowTick();

  const initializedRef = useRef(false);
  const liveBriefIdsRef = useRef<Set<string>>(new Set());

  // The #183 corroboration chip hands this surface an incident bbox.
  useNewsFocus(useCallback((f: SignalFeedFocus) => setFocus(f), []));

  const query = useMemo(() => buildSignalQuery(filters, { eventMode, focus }), [filters, eventMode, focus]);
  const bulletinQuery = useMemo(
    () => buildBulletinQuery(filters, { eventMode, focus }),
    [filters, eventMode, focus],
  );
  const clusterQuery = useMemo(
    () => buildClusterQuery(filters, { eventMode, focus }),
    [filters, eventMode, focus],
  );

  // Route LATEST (band + corroboration + new-since counts) and DISPLAYED (streams).
  const latest = useMemo(() => routeSignalsByStrength(signals), [signals]);
  const shown = useMemo(() => routeSignalsByStrength(displayed), [displayed]);
  const urgentOfficialSignals = latest.urgentOfficial;
  const officialSignals = latest.official;

  const clusterKeys = useMemo(() => new Set(clusters.map((c) => c.clusterKey)), [clusters]);

  // #561 brief pin lifecycle — LIVE briefs stay in the Briefs tab's live zone; a
  // resolved/dismissed/orphan brief demotes to the "Earlier briefs" divider (this
  // re-targets #561's demote destination — briefs NEVER cross into News & Wire).
  const { pinnedBulletins, streamBulletins } = useMemo(() => {
    const pinned: MediaSignalBulletin[] = [];
    const stream: MediaSignalBulletin[] = [];
    for (const b of bulletins) {
      if (b.anomalyStatus != null && LIVE_ANOMALY_STATUSES.has(b.anomalyStatus)) pinned.push(b);
      else stream.push(b);
    }
    stream.sort((a, b) => new Date(b.generatedAt).getTime() - new Date(a.generatedAt).getTime());
    return { pinnedBulletins: pinned, streamBulletins: stream };
  }, [bulletins]);

  // Briefs-tab SOCIAL stream (clustered posts) + raw chatter — from DISPLAYED.
  const { clusteredSurge, rawChatter } = useMemo(
    () => partitionSurge(shown.surge, clusterKeys),
    [shown.surge, clusterKeys],
  );
  const socialStream = useMemo(() => [...clusteredSurge].sort(byTimeDesc), [clusteredSurge]);

  // News-tab STORY stream — DISPLAYED headline (GDELT/RSS + folded weak-official),
  // story-collapsed by the API, strictly time-sorted. HARD INVARIANT: no social.
  const newsStream = useMemo(() => [...shown.headline].sort(byTimeDesc), [shown.headline]);

  // New-since counts (latest not yet displayed), per tab — the reveal pills.
  const displayedIds = useMemo(() => new Set(displayed.map((s) => s.id)), [displayed]);
  const newsNew = useMemo(
    () => latest.headline.filter((s) => !displayedIds.has(s.id)).length,
    [latest.headline, displayedIds],
  );
  const socialNew = useMemo(() => {
    const { clusteredSurge: latestClustered } = partitionSurge(latest.surge, clusterKeys);
    return latestClustered.filter((s) => !displayedIds.has(s.id)).length;
  }, [latest.surge, clusterKeys, displayedIds]);

  const fresh = computeFeedFreshness(freshness.data, freshness.receivedAt, nowMs);
  const liveBriefCount = pinnedBulletins.length;

  useEffect(() => {
    let cancelled = false;
    const ctl = new AbortController();
    // The FIRST load under THIS query (a filter / event-mode / focus change re-runs
    // the effect) reflects immediately into `displayed` — an explicit view change is
    // not a "new arrival" to gate behind the pill. Subsequent POLLS on the same
    // query freeze `displayed` so the streams don't auto-insert.
    let firstLoadOfThisQuery = true;
    const load = async (): Promise<void> => {
      let loadedSignals: MediaSignal[] | null = null;
      let loadedBulletins: MediaSignalBulletin[] = [];
      try {
        const res = await fetch(`/api/media-signals?${query}`, { signal: ctl.signal, cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { signals: MediaSignal[]; freshness?: MediaSignalFreshness };
        if (!cancelled) {
          loadedSignals = body.signals;
          setSignals(body.signals);
          // SERVER-driven freshness (last-good on failure — see the catch: state
          // stays 'error' but the previous freshness envelope + stamp remain).
          if (body.freshness) setFreshness({ data: body.freshness, receivedAt: Date.now() });
          setState('ready');
        }
      } catch {
        if (!cancelled && !ctl.signal.aborted) setState('error');
      }
      // Surge bulletins (#65) are ADDITIVE — a failure leaves the raw wire untouched.
      try {
        const res = await fetch(`/api/media-signal-bulletins?${bulletinQuery}`, { signal: ctl.signal, cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { bulletins?: MediaSignalBulletin[] };
        loadedBulletins = body.bulletins ?? [];
        if (!cancelled) setBulletins(loadedBulletins);
      } catch {
        if (!cancelled && !ctl.signal.aborted) setBulletins([]);
      }
      // Surge clusters (#349) are ADDITIVE too.
      try {
        const res = await fetch(`/api/media-signal-clusters?${clusterQuery}`, { signal: ctl.signal, cache: 'no-store' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const body = (await res.json()) as { clusters?: MediaSignalCluster[] };
        if (!cancelled) setClusters(body.clusters ?? []);
      } catch {
        if (!cancelled && !ctl.signal.aborted) setClusters([]);
      }
      // Reflect the new view immediately on the FIRST load of a (changed) query, so
      // a narrowing filter / focus never leaves the streams showing stale cards that
      // contradict the active filter (and the empty state can honestly appear).
      if (!cancelled && firstLoadOfThisQuery && loadedSignals != null) {
        firstLoadOfThisQuery = false;
        setDisplayed(loadedSignals);
        setHighlightIds(new Set()); // a filter change is not a "reveal" — no highlight
      }
      // FIRST-EVER cycle: pick the default tab (Briefs iff ≥1 live brief at first
      // render, else News). NEVER auto-switch after this — only an operator tap.
      if (!cancelled && !initializedRef.current && loadedSignals != null) {
        initializedRef.current = true;
        const liveIds = new Set(
          loadedBulletins
            .filter((b) => b.anomalyStatus != null && LIVE_ANOMALY_STATUSES.has(b.anomalyStatus))
            .map((b) => b.id),
        );
        liveBriefIdsRef.current = liveIds;
        setActiveTab(liveIds.size >= 1 ? 'briefs' : 'news');
      }
    };
    setState('loading');
    void load();
    const id = setInterval(() => void load(), POLL_MS);
    return () => {
      cancelled = true;
      ctl.abort();
      clearInterval(id);
    };
  }, [query, bulletinQuery, clusterQuery]);

  // Hidden-tab BLINK (#563) — only when a brief NEWLY goes live while the Briefs
  // tab is hidden (a strong-official lands in the cross-tab band, visible from
  // both tabs, so it never needs a blink). The News tab has no badge/blink.
  useEffect(() => {
    if (!initializedRef.current) return;
    const current = new Set(pinnedBulletins.map((b) => b.id));
    let hasNew = false;
    for (const id of current) if (!liveBriefIdsRef.current.has(id)) hasNew = true;
    liveBriefIdsRef.current = current;
    if (hasNew && activeTab !== 'briefs') setBriefsBlink(true);
  }, [pinnedBulletins, activeTab]);

  // One-shot decaying highlight on revealed cards — cleared after the CSS fade.
  useEffect(() => {
    if (highlightIds.size === 0) return;
    const t = setTimeout(() => setHighlightIds(new Set()), 1500);
    return () => clearTimeout(t);
  }, [highlightIds]);

  const reveal = useCallback(() => {
    const dispIds = new Set(displayed.map((s) => s.id));
    const fresh = new Set(signals.filter((s) => !dispIds.has(s.id)).map((s) => s.id));
    setDisplayed(signals);
    setHighlightIds(fresh);
  }, [displayed, signals]);

  const selectTab = useCallback((tab: Tab) => {
    setActiveTab(tab);
    if (tab === 'briefs') setBriefsBlink(false);
  }, []);

  const [undoBeat, setUndoBeat] = useState<{ signal: MediaSignal; index: number } | null>(null);
  useEffect(() => {
    if (!undoBeat) return;
    const t = setTimeout(() => setUndoBeat(null), UNDO_BEAT_MS);
    return () => clearTimeout(t);
  }, [undoBeat]);

  const dismiss = useCallback(
    (signal: MediaSignal) => {
      // #563 dismiss acts on the whole story group server-side (dedup_key); here we
      // optimistically drop the lead from BOTH the latest + displayed snapshots.
      const index = displayed.findIndex((s) => s.id === signal.id);
      setSignals((prev) => prev.filter((s) => s.id !== signal.id));
      setDisplayed((prev) => prev.filter((s) => s.id !== signal.id));
      setUndoBeat({ signal, index: index < 0 ? 0 : index });
      void fetch(`/api/media-signals/${encodeURIComponent(signal.id)}/dismiss`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: getSessionId() }),
      }).catch(() => {});
      logAction('dismiss_media_signal', { signal_id: signal.id, source: signal.source });
    },
    [displayed],
  );

  const undo = useCallback(() => {
    if (!undoBeat) return;
    const { signal, index } = undoBeat;
    setUndoBeat(null);
    const reinsert = (prev: MediaSignal[]): MediaSignal[] => {
      if (prev.some((s) => s.id === signal.id)) return prev;
      const next = [...prev];
      next.splice(Math.min(index, next.length), 0, signal);
      return next;
    };
    setSignals(reinsert);
    setDisplayed(reinsert);
    void fetch(`/api/media-signals/${encodeURIComponent(signal.id)}/dismissal`, { method: 'DELETE' }).catch(() => {});
    logAction('restore_media_signal', { signal_id: signal.id, source: signal.source });
  }, [undoBeat]);

  const pin = useCallback(
    (signal: MediaSignal) => {
      const wasPinned = isPinned('news', signal.id);
      togglePin({ kind: 'news', id: signal.id, label: signalDisplayTitle(signal) });
      if (!wasPinned) {
        logAction('pin_media_signal', { signal_id: signal.id, source: signal.source, dedup_key: signal.dedupKey });
      }
    },
    [togglePin, isPinned],
  );

  const briefsHasContent =
    pinnedBulletins.length > 0 ||
    clusters.length > 0 ||
    streamBulletins.length > 0 ||
    socialStream.length > 0 ||
    rawChatter.length > 0;

  return (
    <section
      data-testid={`news-social-feed-${surface}`}
      className="flex h-full min-h-0 flex-col bg-slate-900/40 text-slate-100"
    >
      {/* ── FIXED HEADER STACK (never scrolls with a tab) ── */}
      <FeedFilters filters={filters} eventMode={eventMode} onFilters={setFilters} onEventMode={setEventMode} />
      {focus && (
        <div
          data-testid="news-focus-chip"
          className="mx-2 mb-1 flex shrink-0 items-center justify-between gap-2 rounded border border-sky-700/60 bg-sky-950/40 px-2 py-1"
        >
          <span className="min-w-0 truncate text-[11px] text-sky-200">Near {focus.label}</span>
          <button
            type="button"
            data-testid="news-focus-clear"
            onClick={() => setFocus(null)}
            title="Clear the incident focus"
            className="shrink-0 rounded px-1 text-[11px] text-sky-300 hover:text-sky-100"
          >
            ✕
          </button>
        </div>
      )}
      {undoBeat && (
        <div
          data-testid="dismiss-undo-beat"
          className="mx-2 mb-1 flex shrink-0 items-center justify-between gap-2 rounded border border-slate-700/60 bg-slate-800/60 px-2 py-1"
        >
          <span className="min-w-0 truncate text-[11px] text-slate-300">
            Dismissed “{signalDisplayTitle(undoBeat.signal)}”
          </span>
          <button
            type="button"
            data-testid="dismiss-undo"
            onClick={undo}
            title="Restore this signal to the feed"
            className="shrink-0 rounded px-1 text-[11px] font-medium text-sky-300 hover:text-sky-100"
          >
            Undo
          </button>
        </div>
      )}

      {/* CROSS-TAB URGENT-OFFICIAL BAND (#563) — moved OUT of the scroll, above the
          tabs, visible from BOTH tabs; absent-when-empty; max-h + internal scroll.
          Verified authority + operator review, NEVER an auto-scored Response Level. */}
      {urgentOfficialSignals.length > 0 && (
        <section
          data-testid="feed-band-official-urgent"
          className="mx-2 mb-1 max-h-40 shrink-0 overflow-y-auto rounded-md border border-emerald-600/70 bg-emerald-950/30 p-2 ring-1 ring-emerald-700/40"
        >
          <div className="sticky top-0 mb-1.5 flex items-baseline gap-1.5 border-b border-emerald-800/50 bg-emerald-950/60 pb-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-200">
              <span aria-hidden>🛡</span> {V.OFFICIAL_ALERTS_BAND_HEADER}
            </span>
            <span className="text-[10px] italic text-emerald-400/70">{V.OFFICIAL_ALERTS_BAND_SUBLABEL}</span>
          </div>
          <ul data-testid="official-urgent-list" className="space-y-1.5">
            {urgentOfficialSignals.map((s) => (
              <SignalCard key={s.id} signal={s} official pinned={isPinned('news', s.id)} onPin={pin} onDismiss={dismiss} />
            ))}
          </ul>
        </section>
      )}

      {/* ASK THE WIRE (#299/#332) — in the header; its answer block gains max-h +
          internal scroll so a long answer never pushes the tabs off-screen. */}
      <div className="mx-2 mb-1 max-h-72 shrink-0 overflow-y-auto">
        <AskTheWire filters={filters} focus={focus} />
      </div>

      {/* ── TAB BAR + live/stale stamp ── */}
      <div
        data-testid="feed-tabbar"
        role="tablist"
        className="flex shrink-0 items-center gap-1 border-b border-slate-700 bg-slate-900/80 px-2"
      >
        <TabButton
          tab="briefs"
          label={`⚡ ${V.SITUATION_BRIEFS_TAB}`}
          badge={liveBriefCount > 0 ? liveBriefCount : null}
          active={activeTab === 'briefs'}
          blinking={briefsBlink}
          onSelect={selectTab}
        />
        <TabButton tab="news" label={V.NEWS_WIRE_TAB} badge={null} active={activeTab === 'news'} blinking={false} onSelect={selectTab} />
        <span data-testid="feed-freshness" className="ml-auto shrink-0 py-1 text-[10px]">
          {fresh.isStale ? (
            <span
              data-testid="feed-stale-badge"
              title="The server's newest data is older than 10 minutes — ingestion may be stalled."
              className="rounded bg-amber-900/60 px-1.5 py-0.5 font-semibold uppercase tracking-wide text-amber-200"
            >
              {V.staleBadge(fresh.ageMinutes ?? 0)}
            </span>
          ) : fresh.updatedLabel ? (
            <span data-testid="feed-live-stamp" className="text-emerald-400/80" title="Newest wire data time (server clock)">
              {V.liveStamp(fresh.updatedLabel)}
            </span>
          ) : null}
        </span>
      </div>

      {/* ── BOTH PANELS STAY MOUNTED (no auto-switch) ── */}
      <div
        data-testid="briefs-panel"
        className={activeTab === 'briefs' ? 'min-h-0 flex-1 overflow-y-auto px-2 pb-2 pt-1' : 'hidden'}
      >
        {socialNew > 0 && <NewSincePill testid="briefs-new-pill" count={socialNew} onReveal={reveal} />}
        {briefsHasContent ? (
          <>
            {pinnedBulletins.length > 0 && (
              <ul data-testid="bulletin-list" className="space-y-1.5">
                {pinnedBulletins.map((b) => (
                  <BulletinCard key={b.id} bulletin={b} />
                ))}
              </ul>
            )}
            {clusters.length > 0 && (
              <ul data-testid="surge-card-list" className="mt-1.5 space-y-1.5">
                {clusters.map((c) => (
                  <SurgeCard key={c.clusterKey} cluster={c} />
                ))}
              </ul>
            )}
            {streamBulletins.length > 0 && (
              <section data-testid="earlier-briefs" className="mt-2">
                <div
                  data-testid="earlier-briefs-divider"
                  className="mb-1.5 text-center text-[10px] font-medium uppercase tracking-wide text-slate-500"
                >
                  {V.EARLIER_BRIEFS_DIVIDER}
                </div>
                <ul data-testid="earlier-briefs-list" className="space-y-1.5">
                  {streamBulletins.map((b) => (
                    <li key={b.id} data-testid="earlier-brief-item">
                      <BulletinCard bulletin={b} />
                      <p className="mt-0.5 pl-1 text-[9px] italic text-slate-600">{V.SURGE_OVER_SUBLABEL}</p>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {socialStream.length > 0 && (
              <section data-testid="social-media-section" className="mt-2">
                <h3 className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-violet-300/80">
                  {V.SOCIAL_MEDIA_SECTION}
                </h3>
                <ul data-testid="social-stream" className="space-y-1.5">
                  {socialStream.map((s) => (
                    <SignalCard
                      key={s.id}
                      signal={s}
                      trending
                      corroboration={corroboratingOfficialSignals(s, officialSignals)}
                      highlighted={highlightIds.has(s.id)}
                      pinned={isPinned('news', s.id)}
                      onPin={pin}
                      onDismiss={dismiss}
                    />
                  ))}
                </ul>
              </section>
            )}
            {rawChatter.length > 0 && (
              <RawChatterSection posts={rawChatter} isPinned={isPinned} onPin={pin} onDismiss={dismiss} />
            )}
          </>
        ) : state === 'error' ? (
          <p data-testid="news-feed-error" className="px-1 py-3 text-[11px] text-slate-500">
            {V.WIRE_UNAVAILABLE}
          </p>
        ) : state === 'loading' ? (
          <p className="px-1 py-3 text-[11px] text-slate-500">{V.WIRE_LOADING}</p>
        ) : (
          <div data-testid="briefs-quiet-state" className="px-1 py-3">
            <p className="text-[11px] text-slate-500">{V.BRIEFS_QUIET_STATE}</p>
            {fresh.updatedLabel && (
              <p className="mt-0.5 text-[10px] text-slate-600">{V.wireLiveAsOf(fresh.updatedLabel)}</p>
            )}
          </div>
        )}
      </div>

      <div
        data-testid="news-panel"
        className={activeTab === 'news' ? 'min-h-0 flex-1 overflow-y-auto px-2 pb-2 pt-1' : 'hidden'}
      >
        {newsNew > 0 && <NewSincePill testid="news-new-pill" count={newsNew} onReveal={reveal} />}
        {newsStream.length > 0 ? (
          <ul data-testid="news-story-stream" className="space-y-1.5">
            {newsStream.map((s) => (
              <StoryCard
                key={s.id}
                signal={s}
                official={isOfficialSignal(s)}
                highlighted={highlightIds.has(s.id)}
                pinned={isPinned('news', s.id)}
                onPin={pin}
                onDismiss={dismiss}
              />
            ))}
          </ul>
        ) : state === 'error' ? (
          <p data-testid="news-feed-error" className="px-1 py-3 text-[11px] text-slate-500">
            {V.WIRE_UNAVAILABLE}
          </p>
        ) : state === 'loading' ? (
          <p className="px-1 py-3 text-[11px] text-slate-500">{V.WIRE_LOADING}</p>
        ) : (
          <p data-testid="news-feed-empty" className="px-1 py-3 text-[11px] text-slate-500">
            {focus ? V.NEWS_EMPTY_FOCUS_STATE : V.NEWS_EMPTY_STATE}
          </p>
        )}
      </div>
    </section>
  );
}

/** Tab bar button (StackedQueueTabs idiom) — active underline, an optional LIVE
 *  count badge, and a one-shot blink when a hidden tab has a new live brief. */
function TabButton({
  tab,
  label,
  badge,
  active,
  blinking,
  onSelect,
}: {
  tab: Tab;
  label: string;
  badge: number | null;
  active: boolean;
  blinking: boolean;
  onSelect: (t: Tab) => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={`tab-${tab}`}
      data-active={active}
      data-blinking={blinking}
      onClick={() => onSelect(tab)}
      className={`relative flex items-center gap-1.5 px-2 py-1.5 text-[12px] font-medium ${
        active ? 'border-b-2 border-sky-400 text-slate-100' : 'border-b-2 border-transparent text-slate-400 hover:text-slate-200'
      } ${blinking ? 'animate-pulse text-amber-300' : ''}`}
    >
      <span>{label}</span>
      {badge != null && (
        <span
          data-testid={`tab-${tab}-badge`}
          className="rounded-full bg-violet-700/70 px-1.5 py-px text-[10px] font-semibold text-violet-100"
        >
          {V.briefsBadge(badge)}
        </span>
      )}
    </button>
  );
}

/** The "▲ N new — show" reveal pill (#563) — a stream NEVER auto-inserts; the
 *  operator clicks to merge the new items (which then get a decaying highlight). */
function NewSincePill({
  testid,
  count,
  onReveal,
}: {
  testid: string;
  count: number;
  onReveal: () => void;
}): JSX.Element {
  return (
    <div className="mb-1.5 flex justify-center">
      <button
        type="button"
        data-testid={testid}
        onClick={onReveal}
        className="rounded-full border border-sky-600/70 bg-sky-950/60 px-3 py-0.5 text-[11px] font-medium text-sky-200 shadow hover:bg-sky-900/60"
      >
        {V.newSincePill(count)}
      </button>
    </div>
  );
}

/**
 * STORY CARD (#563) — one card per dedup group on the News & Wire tab. Leads with
 * the API-chosen authoritative member (✓ official + host never buried); a
 * diversity chip "N outlets · <lead> ×copies" (DISTINCT outlets counted separately
 * from repeat count — repeats are NOT corroboration) expands to the member list;
 * a 📸 chip when any member carries media; Dismiss acts on the whole group (the
 * server dedup-group dismissal). A decaying highlight rings a just-revealed card.
 */
function StoryCard({
  signal,
  official,
  highlighted,
  pinned,
  onPin,
  onDismiss,
}: {
  signal: MediaSignal;
  official: boolean;
  highlighted: boolean;
  pinned: boolean;
  onPin: (s: MediaSignal) => void;
  onDismiss: (s: MediaSignal) => void;
}): JSX.Element {
  const [membersOpen, setMembersOpen] = useState(false);
  const body = signalBodyExcerpt(signal);
  const story = signal.story;
  const showChip = !!story && (story.copies > 1 || story.distinctOutlets > 1);
  const anyMedia = !!story?.hasMedia || hasSignalMedia(signal);
  return (
    <li
      data-testid="story-card"
      data-signal-id={signal.id}
      data-dedup-key={signal.dedupKey}
      className={`rounded-md border bg-slate-950/60 p-2 transition-[box-shadow] duration-1000 motion-reduce:transition-none ${
        official ? 'border-emerald-800/50' : 'border-slate-700/80'
      } ${highlighted ? 'ring-2 ring-sky-400/70' : 'ring-0'}`}
    >
      <div className="flex items-center gap-1.5">
        <span
          data-testid="signal-source"
          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
            official ? 'bg-emerald-900/60 text-emerald-200' : 'bg-slate-800 text-slate-300'
          }`}
        >
          {official ? officialSourceLabel(signal) : signalSourceLabel(signal.source)}
        </span>
        {official && (
          <span
            data-testid="signal-official"
            title="Official government / agency feed — authoritative source"
            className="shrink-0 rounded bg-emerald-800/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-200"
          >
            ✓ official
          </span>
        )}
        {anyMedia && (
          <span
            data-testid="story-media-chip"
            title="A photo or video is attached to a copy of this story"
            className="shrink-0 rounded bg-sky-900/50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-sky-300"
          >
            📸 media
          </span>
        )}
        {signal.language !== 'und' && (
          <span
            data-testid="signal-language"
            className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
              signal.language === 'fr' ? 'bg-indigo-900/70 text-indigo-200' : 'bg-slate-800 text-slate-400'
            }`}
          >
            {signal.language}
          </span>
        )}
        <span className="min-w-0 truncate text-[10px] text-slate-500" title={new Date(signalTime(signal)).toLocaleString()}>
          {relTime(signalTime(signal))}
        </span>
        {signal.url && (
          <a
            href={signal.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="signal-link-out"
            title="Open the source"
            className="ml-auto shrink-0 text-[11px] text-sky-400 hover:text-sky-300"
          >
            source ↗
          </a>
        )}
      </div>
      <p className="mt-1 text-[12px] font-medium leading-snug text-slate-100">{signalDisplayTitle(signal)}</p>
      {body && <p className="mt-0.5 text-[11px] leading-snug text-slate-400">{body}</p>}
      {signal.geoName && (
        <p data-testid="signal-geo" className="mt-0.5 text-[10px] text-slate-500">
          📍 {signal.geoName}
          {!signal.geo && ' (no map point)'}
        </p>
      )}
      {showChip && story && (
        <div className="mt-1">
          <button
            type="button"
            data-testid="story-diversity-chip"
            aria-expanded={membersOpen}
            onClick={() => setMembersOpen((o) => !o)}
            title="Distinct outlets carrying this story — repeats from one outlet are not corroboration"
            className="flex items-center gap-1 rounded-full border border-slate-600/80 px-1.5 py-px text-[10px] text-slate-300 hover:border-slate-400"
          >
            <span aria-hidden>{membersOpen ? '▾' : '▸'}</span>
            {V.diversityChip(story.distinctOutlets, story.leadOutlet, story.copies)}
          </button>
          {membersOpen && (
            <ul data-testid="story-members" className="mt-1 space-y-0.5 border-l border-slate-700/60 pl-2">
              {story.members.map((m, i) => (
                <li key={`${m.source}:${i}`} className="flex items-baseline gap-1.5 text-[10px]">
                  <span className="shrink-0 rounded bg-slate-800 px-1 py-px text-[9px] uppercase text-slate-400">
                    {m.outlet}
                  </span>
                  {m.url ? (
                    <a href={m.url} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate text-sky-400 hover:text-sky-300">
                      {m.title ?? m.url} ↗
                    </a>
                  ) : (
                    <span className="min-w-0 truncate text-slate-300">{m.title ?? '(untitled)'}</span>
                  )}
                  <span className="ml-auto shrink-0 text-slate-500">{relTime(m.publishedAt ?? m.ingestedAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="mt-1.5 flex items-center gap-1">
        {signal.hazardKeywords.map((k) => (
          <span
            key={k}
            data-testid="signal-hazard-chip"
            className="rounded-full border border-slate-600/80 px-1.5 py-px text-[10px] text-slate-300"
          >
            {findHazardLexiconEntry(k)?.label ?? k}
          </span>
        ))}
        <span className="ml-auto flex shrink-0 items-center gap-1">
          <button
            type="button"
            data-testid="signal-pin"
            onClick={() => onPin(signal)}
            title={pinned ? 'Unpin from the media region' : 'Pin to the media region / wall'}
            className={`rounded border px-1.5 py-0.5 text-[10px] ${
              pinned ? 'border-sky-600 bg-sky-900/40 text-sky-200' : 'border-slate-600 text-slate-300 hover:border-slate-400 hover:text-slate-100'
            }`}
          >
            {pinned ? 'Pinned' : 'Pin'}
          </button>
          <button
            type="button"
            data-testid="signal-dismiss"
            onClick={() => onDismiss(signal)}
            title="Hide this whole story from the feed (all seats)"
            className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400 hover:border-slate-500 hover:text-slate-200"
          >
            Dismiss
          </button>
        </span>
      </div>
    </li>
  );
}

/**
 * SURGE COUNT CARD (#349, #555) — a surge-volume absorber pinned above the stream.
 * It reads the count signal ("N posts about <hazard> near <place> · M outlets ·
 * trending · not confirmed") straight from one open igniter cluster — no LLM
 * needed — so the operator sees at a glance that chatter is surging without the
 * stream flooding. The underlying posts render INLINE in the one time-sorted
 * stream (#555 C1); the cluster's optional "what's happening" brief renders as an
 * Emerging Situation Brief card in the same pinned zone. HONESTY: a surge card is
 * NEVER auto-urgent and NEVER scores a Response Level — it reads "trending · not
 * confirmed", full stop.
 */
function SurgeCard({ cluster }: { cluster: MediaSignalCluster }): JSX.Element {
  const hazardLabel = findHazardLexiconEntry(cluster.hazardKey)?.label ?? cluster.hazardKey;
  return (
    <li
      data-testid="surge-card"
      data-cluster-key={cluster.clusterKey}
      className="rounded-md border border-violet-700/60 bg-violet-950/30 p-2"
    >
      <div className="flex items-center gap-1.5">
        <span className="shrink-0 rounded bg-violet-900/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-200">
          ⚡ Surge
        </span>
        <span
          data-testid="surge-count"
          title="Count is the cluster snapshot from the last surge scan — the posts in the stream may differ."
          className="min-w-0 text-[12px] font-medium leading-snug text-slate-100"
        >
          {cluster.count} {cluster.count === 1 ? 'post' : 'posts'} about {hazardLabel}
          {cluster.place ? ` near ${cluster.place}` : ''}
        </span>
        <span
          className="ml-auto shrink-0 text-[10px] text-slate-500"
          title={new Date(cluster.firstSignalAt).toLocaleString()}
        >
          {relTime(cluster.firstSignalAt)}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        <span
          data-testid="surge-outlets"
          className="shrink-0 rounded-full border border-violet-700/60 px-1.5 py-px text-[10px] text-violet-200"
        >
          {cluster.origins} {cluster.origins === 1 ? 'outlet' : 'outlets'}
        </span>
        <span
          data-testid="surge-not-confirmed"
          title="A surge of chatter — corroboration, not confirmation. Operator-assessed."
          className="shrink-0 rounded bg-violet-900/50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-violet-300"
        >
          trending · not confirmed
        </span>
      </div>
    </li>
  );
}

/**
 * RAW CHATTER (#349) — the demoted per-post firehose. Non-clustered social posts
 * (chatter that hasn't formed a qualified surge) collapse into a single
 * de-emphasized, collapsed section so a lone venting post never reads as a
 * first-class hazard card. Pin/dismiss still ride each post.
 */
function RawChatterSection({
  posts,
  isPinned,
  onPin,
  onDismiss,
}: {
  posts: MediaSignal[];
  isPinned: (kind: 'news', id: string) => boolean;
  onPin: (s: MediaSignal) => void;
  onDismiss: (s: MediaSignal) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <section data-testid="raw-chatter-section" className="mt-2">
      <button
        type="button"
        data-testid="raw-chatter-toggle"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 rounded px-1 py-1 text-left text-[10px] font-medium uppercase tracking-wide text-slate-500 hover:text-slate-300"
      >
        <span aria-hidden>{open ? '▾' : '▸'}</span>
        {V.RAW_CHATTER_LABEL} ({posts.length})
        <span className="ml-auto text-[9px] italic normal-case tracking-normal text-slate-600">
          unclustered — not a surge
        </span>
      </button>
      {open && (
        <ul data-testid="raw-chatter-list" className="mt-1.5 space-y-1.5 opacity-80">
          {posts.map((s) => (
            <SignalCard
              key={s.id}
              signal={s}
              pinned={isPinned('news', s.id)}
              onPin={onPin}
              onDismiss={onDismiss}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function FeedFilters({
  filters,
  eventMode,
  onFilters,
  onEventMode,
}: {
  filters: SignalFeedFilters;
  eventMode: boolean;
  onFilters: (f: SignalFeedFilters) => void;
  onEventMode: (on: boolean) => void;
}): JSX.Element {
  const selectClass =
    'min-w-0 flex-1 rounded border border-slate-700 bg-slate-900 px-1 py-0.5 text-[11px] text-slate-200 disabled:opacity-40';
  return (
    <div className="shrink-0 space-y-1 border-b border-slate-700/70 p-2">
      <div className="flex items-center gap-1.5">
        <select
          aria-label="Hazard filter"
          data-testid="news-filter-hazard"
          className={selectClass}
          disabled={eventMode}
          value={filters.hazard}
          onChange={(e) => onFilters({ ...filters, hazard: e.target.value })}
        >
          <option value="all">All hazards</option>
          {HAZARD_LEXICON.map((h) => (
            <option key={h.key} value={h.key}>
              {h.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Language filter"
          data-testid="news-filter-language"
          className={selectClass}
          disabled={eventMode}
          value={filters.language}
          onChange={(e) =>
            onFilters({ ...filters, language: e.target.value as SignalFeedFilters['language'] })
          }
        >
          <option value="all">EN + FR</option>
          <option value="en">English</option>
          <option value="fr">Français</option>
        </select>
      </div>
      <div className="flex items-center gap-1.5">
        <select
          aria-label="Source filter"
          data-testid="news-filter-source"
          className={selectClass}
          disabled={eventMode}
          value={filters.source}
          onChange={(e) =>
            onFilters({ ...filters, source: e.target.value as SignalFeedFilters['source'] })
          }
        >
          <option value="all">All sources</option>
          <option value="gdelt">GDELT</option>
          <option value="rss">News (RSS)</option>
          <option value="bluesky">Bluesky</option>
          <option value="x">X</option>
        </select>
        <select
          aria-label="Since filter"
          data-testid="news-filter-since"
          className={selectClass}
          value={filters.sinceHours}
          onChange={(e) => onFilters({ ...filters, sinceHours: Number(e.target.value) })}
        >
          {SINCE_CHOICES.map((c) => (
            <option key={c.hours} value={c.hours}>
              {c.label}
            </option>
          ))}
        </select>
        <label
          className="flex shrink-0 cursor-pointer items-center gap-1 text-[11px] text-slate-400"
          title="Raw firehose — show the whole wire, ignoring the hazard / language / source filters. The wire itself stores only hazard-matched items."
        >
          <input
            type="checkbox"
            data-testid="news-event-mode"
            checked={eventMode}
            onChange={(e) => onEventMode(e.target.checked)}
            className="accent-sky-500"
          />
          Event mode
        </label>
      </div>
    </div>
  );
}

/** Source-type chip palette for the brief's per-type provenance + evidence tags. */
const SOURCE_TYPE_META: Record<string, { label: string; cls: string }> = {
  news: { label: 'news', cls: 'bg-sky-900/60 text-sky-200' },
  social: { label: 'social', cls: 'bg-violet-900/60 text-violet-200' },
  official: { label: 'official', cls: 'bg-emerald-900/60 text-emerald-200' },
  scanner: { label: 'scanner', cls: 'bg-amber-900/60 text-amber-200' },
};

/**
 * Emerging Situation Brief card (#295, migration 0078) — the bulletin grown up
 * into the distinct card type atop the feed: a cross-source fused, provenance-
 * stamped brief of one igniter-qualified cluster. Honesty rules: ALWAYS carries
 * "AI-generated summary · N sources" chrome; for a fused brief it ALSO shows
 * per-source-TYPE corroboration (news / social / official), the explicit "not
 * yet confirmed" line, the #298 coherence caution when ambiguous, and the "first
 * seen" leading-signal stamp when present. The underlying signals (source-type
 * tagged) expand beneath it. Suppressed clusters render official-context framing
 * — corroboration, never a second alarm. READ-ONLY: the card has no urgent /
 * Response-Level control — a brief is never auto-urgent and never scores a
 * Response Level (operator-decided, council bar). A pre-0078 bulletin (corroboration
 * null) renders the original layout — graceful degradation.
 */
function BulletinCard({ bulletin }: { bulletin: MediaSignalBulletin }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const hazardLabel =
    bulletin.language === 'fr'
      ? findHazardLexiconEntry(bulletin.hazardKey)?.labelFr
      : findHazardLexiconEntry(bulletin.hazardKey)?.label;
  const corr = bulletin.corroboration;
  const isBrief = bulletin.isBrief && corr != null;
  return (
    <li
      data-testid="bulletin-card"
      data-bulletin-id={bulletin.id}
      data-brief={isBrief ? 'true' : 'false'}
      className="rounded-md border border-violet-700/60 bg-violet-950/30 p-2"
    >
      <div className="flex items-center gap-1.5">
        <span className="shrink-0 rounded bg-violet-900/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-200">
          ⚡ {isBrief ? 'Brief' : 'Surge'}
        </span>
        <span
          data-testid="bulletin-hazard"
          className="shrink-0 rounded-full border border-violet-700/60 px-1.5 py-px text-[10px] text-violet-200"
        >
          {hazardLabel ?? bulletin.hazardKey}
        </span>
        {bulletin.language === 'fr' && (
          <span className="shrink-0 rounded bg-indigo-900/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-indigo-200">
            fr
          </span>
        )}
        <span
          className="ml-auto min-w-0 shrink-0 truncate text-[10px] text-slate-500"
          title={new Date(bulletin.generatedAt).toLocaleString()}
        >
          {relTime(bulletin.generatedAt)}
        </span>
      </div>
      <p className="mt-1 text-[12px] leading-snug text-slate-100">{bulletin.summary}</p>
      {/* Per-source-TYPE corroboration — the brief SEPARATES what each type says. */}
      {isBrief && corr && (
        <div data-testid="brief-provenance" className="mt-1 flex flex-wrap items-center gap-1">
          <span
            data-testid="brief-prov-news"
            className={`shrink-0 rounded px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide ${SOURCE_TYPE_META.news!.cls}`}
          >
            📰 {corr.news.count} news
          </span>
          <span
            data-testid="brief-prov-social"
            className={`shrink-0 rounded px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide ${SOURCE_TYPE_META.social!.cls}`}
          >
            💬 {corr.social.count} social
          </span>
          <span
            data-testid="brief-prov-official"
            className={`shrink-0 rounded px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide ${
              corr.official.present ? SOURCE_TYPE_META.official!.cls : 'bg-slate-800 text-slate-400'
            }`}
          >
            🛡 {corr.official.present ? 'official ✓' : 'no official feed'}
          </span>
        </div>
      )}
      {/* "First seen" leading-signal stamp (#298) — only on a coherent, pre-official brief. */}
      {bulletin.leadingSignalAt && (
        <p data-testid="brief-leading-signal" className="mt-0.5 text-[10px] font-medium text-amber-300/90">
          ⏱ First seen {relTime(bulletin.leadingSignalAt)} — ahead of any official feed
        </p>
      )}
      {/* #298 coherence caution — possible GDELT place-name conflation. */}
      {bulletin.coherence?.verdict === 'ambiguous' && (
        <p data-testid="brief-coherence" className="mt-0.5 text-[10px] text-amber-400/80">
          ⚠ Possible place-name conflation
          {bulletin.coherence.provinces.length > 0
            ? ` (${bulletin.coherence.provinces.join(', ')})`
            : ''}{' '}
          — may be more than one event.
        </p>
      )}
      {/* The explicit "not yet confirmed" line — uncertainty is first-class (§3.6). */}
      {bulletin.uncertaintyLine && (
        <p data-testid="brief-uncertainty" className="mt-0.5 text-[10px] font-medium text-slate-300">
          {bulletin.uncertaintyLine}
        </p>
      )}
      {bulletin.suppressed && (
        <p data-testid="bulletin-suppressed" className="mt-0.5 text-[10px] text-slate-400">
          ⓘ Officials are already reporting this — shown as corroboration, not a new alarm.
        </p>
      )}
      {/* The honesty chrome — never rendered without it. */}
      <div className="mt-1.5 flex items-center gap-2">
        <span data-testid="bulletin-ai-chrome" className="text-[10px] italic text-violet-300/80">
          AI-generated summary · {bulletin.sourceCount} sources
        </span>
        <button
          type="button"
          data-testid="bulletin-evidence-toggle"
          onClick={() => setExpanded((e) => !e)}
          className="ml-auto shrink-0 rounded border border-slate-600 px-1.5 py-0.5 text-[10px] text-slate-300 hover:border-slate-400 hover:text-slate-100"
        >
          {expanded ? 'Hide sources' : `Show ${bulletin.evidence.length} source signals`}
        </button>
      </div>
      {expanded && (
        <ul data-testid="bulletin-evidence" className="mt-1.5 space-y-1 border-t border-violet-800/40 pt-1.5">
          {bulletin.evidence.map((ev) => {
            const typeMeta = ev.sourceType ? SOURCE_TYPE_META[ev.sourceType] : undefined;
            return (
              <li key={ev.id} className="flex items-baseline gap-1.5 text-[11px]">
                {typeMeta && (
                  <span
                    data-testid="evidence-type"
                    className={`shrink-0 rounded px-1 py-px text-[8px] font-semibold uppercase ${typeMeta.cls}`}
                  >
                    {typeMeta.label}
                  </span>
                )}
                <span className="shrink-0 rounded bg-slate-800 px-1 py-px text-[9px] uppercase text-slate-400">
                  {ev.origin}
                </span>
                {ev.url ? (
                  <a
                    href={ev.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="min-w-0 truncate text-sky-400 hover:text-sky-300"
                  >
                    {ev.title ?? ev.url}
                  </a>
                ) : (
                  <span className="min-w-0 truncate text-slate-300">{ev.title ?? '(untitled)'}</span>
                )}
                <span className="ml-auto shrink-0 text-[10px] text-slate-500">
                  {relTime(ev.observedAt)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

function SignalCard({
  signal,
  pinned,
  onPin,
  onDismiss,
  trending = false,
  official = false,
  corroboration = [],
  highlighted = false,
}: {
  signal: MediaSignal;
  pinned: boolean;
  onPin: (s: MediaSignal) => void;
  onDismiss: (s: MediaSignal) => void;
  /** Surge-lane (social) card — render the honest "trending · not confirmed"
   *  tag so social chatter is never read as confirmation. */
  trending?: boolean;
  /** Official-lane (#297) card — authoritative government/agency feed: emerald
   *  framing, issuing-authority host label, "✓ official" badge. */
  official?: boolean;
  /** Official signals corroborating THIS surge card (#297) — when non-empty a
   *  "✓ officials reporting" chip is shown (hazard-family corroboration in the
   *  window). Surge lane only; never implies the specific post is confirmed. */
  corroboration?: MediaSignal[];
  /** #563 — a one-shot decaying highlight ring on a just-revealed card. */
  highlighted?: boolean;
}): JSX.Element {
  const body = signalBodyExcerpt(signal);
  // #361 thumbnail: a media post renders its photo / video poster. Hidden if the
  // upstream image 404s/blocks (onError) so a dead thumbnail never shows a broken
  // icon. no-referrer keeps the operator's request off the publisher's logs; a
  // video shows a POSTER with a ▶ overlay (never auto-played).
  const [imgFailed, setImgFailed] = useState(false);
  const showMedia = !!signal.media && !imgFailed;
  return (
    <li
      data-testid="signal-card"
      data-signal-id={signal.id}
      className={`rounded-md border bg-slate-950/60 p-2 transition-[box-shadow] duration-1000 motion-reduce:transition-none ${
        official ? 'border-emerald-800/50' : trending ? 'border-violet-800/50' : 'border-slate-700/80'
      } ${highlighted ? 'ring-2 ring-sky-400/70' : 'ring-0'}`}
    >
      {/* Provenance chrome FIRST — source · language · time · link out. */}
      <div className="flex items-center gap-1.5">
        <span
          data-testid="signal-source"
          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
            official ? 'bg-emerald-900/60 text-emerald-200' : 'bg-slate-800 text-slate-300'
          }`}
        >
          {official ? officialSourceLabel(signal) : signalSourceLabel(signal.source)}
        </span>
        {official && (
          <span
            data-testid="signal-official"
            title="Official government / agency feed — authoritative source"
            className="shrink-0 rounded bg-emerald-800/60 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-200"
          >
            ✓ official
          </span>
        )}
        {trending && (
          <span
            data-testid="signal-trending"
            title="Surging / trending on social — corroboration, not confirmation"
            className="shrink-0 rounded bg-violet-900/50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-violet-300"
          >
            trending · not confirmed
          </span>
        )}
        {trending && corroboration.length > 0 && (
          <span
            data-testid="signal-corroborated"
            title={`An official feed is reporting this hazard in the same window — corroboration, not confirmation of this post: ${corroboration
              .map((c) => officialSourceLabel(c))
              .join(', ')}`}
            className="shrink-0 rounded bg-emerald-900/50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-emerald-300"
          >
            ✓ officials reporting
          </span>
        )}
        {/* "📸 media" chip (#555) — a photo/video is attached. Metadata only: the
            thumbnail still renders below; ordering is NEVER changed by it. */}
        {hasSignalMedia(signal) && (
          <span
            data-testid="signal-media-chip"
            title="A photo or video is attached to this post"
            className="shrink-0 rounded bg-sky-900/50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-sky-300"
          >
            📸 media
          </span>
        )}
        {signal.language !== 'und' && (
          <span
            data-testid="signal-language"
            className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
              signal.language === 'fr'
                ? 'bg-indigo-900/70 text-indigo-200'
                : 'bg-slate-800 text-slate-400'
            }`}
          >
            {signal.language}
          </span>
        )}
        <span
          className="min-w-0 truncate text-[10px] text-slate-500"
          title={new Date(signalTime(signal)).toLocaleString()}
        >
          {relTime(signalTime(signal))}
        </span>
        {signal.url && (
          <a
            href={signal.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="signal-link-out"
            title="Open the source"
            className="ml-auto shrink-0 text-[11px] text-sky-400 hover:text-sky-300"
          >
            source ↗
          </a>
        )}
      </div>
      <p className="mt-1 text-[12px] font-medium leading-snug text-slate-100">
        {signalDisplayTitle(signal)}
      </p>
      {body && <p className="mt-0.5 text-[11px] leading-snug text-slate-400">{body}</p>}
      {showMedia && signal.media && (
        <a
          href={signal.url ?? signal.media.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="signal-media-thumb"
          data-media-kind={signal.media.kind}
          title="Open the source"
          className="relative mt-1.5 block overflow-hidden rounded border border-slate-700/60"
        >
          <img
            src={signal.media.url}
            alt={signalDisplayTitle(signal)}
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
            onError={() => setImgFailed(true)}
            className="max-h-32 w-full object-cover"
          />
          {signal.media.kind === 'video' && (
            <span
              aria-hidden
              data-testid="signal-media-video"
              className="pointer-events-none absolute inset-0 flex items-center justify-center text-2xl text-white/90 drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]"
            >
              ▶
            </span>
          )}
        </a>
      )}
      {signal.geoName && (
        <p data-testid="signal-geo" className="mt-0.5 text-[10px] text-slate-500">
          📍 {signal.geoName}
          {!signal.geo && ' (no map point)'}
        </p>
      )}
      <div className="mt-1.5 flex items-center gap-1">
        {signal.hazardKeywords.map((k) => (
          <span
            key={k}
            data-testid="signal-hazard-chip"
            className="rounded-full border border-slate-600/80 px-1.5 py-px text-[10px] text-slate-300"
          >
            {findHazardLexiconEntry(k)?.label ?? k}
          </span>
        ))}
        <span className="ml-auto flex shrink-0 items-center gap-1">
          <button
            type="button"
            data-testid="signal-pin"
            onClick={() => onPin(signal)}
            title={pinned ? 'Unpin from the media region' : 'Pin to the media region / wall'}
            className={`rounded border px-1.5 py-0.5 text-[10px] ${
              pinned
                ? 'border-sky-600 bg-sky-900/40 text-sky-200'
                : 'border-slate-600 text-slate-300 hover:border-slate-400 hover:text-slate-100'
            }`}
          >
            {pinned ? 'Pinned' : 'Pin'}
          </button>
          <button
            type="button"
            data-testid="signal-dismiss"
            onClick={() => onDismiss(signal)}
            title="Hide this signal from the feed (all seats)"
            className="rounded border border-slate-700 px-1.5 py-0.5 text-[10px] text-slate-400 hover:border-slate-500 hover:text-slate-200"
          >
            Dismiss
          </button>
        </span>
      </div>
    </li>
  );
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}
